import type { ChakraStyledOptions } from "@chakra-ui/react";
import { Box, Flex } from "@chakra-ui/react";
import SCImage from "commons/sc/SCImage";
import VideoPlayer from "commons/ui/VideoPlayer";
import BundleIcon from "commons/ui/BundleIcon";
import SCLinkButton from "commons/sc/SCLinkButton";
import SCText from "commons/sc/SCText";
import SCHeading from "commons/sc/SCHeading";
import SCRichText from "commons/sc/SCRichText";
import type { PartnerResult } from "components/PartnersFilter";
import { useSitecoreContext } from "@sitecore-jss/sitecore-jss-nextjs";
interface LinksProps extends ChakraStyledOptions {
    partner: PartnerResult;
}

const Links = ({ partner, ...props }: LinksProps) => {
    const website = partner.websiteLink.jsonValue;
    const linkedin = partner.linkedInLink.jsonValue;
    const downloadLink = partner.downloadLink.jsonValue;
    return (
        <Flex gap={["sp24", null, "sp42"]} marginTop={["sp24", null, "0"]} {...props}>
            {website && (
                <SCLinkButton
                    linkField={website}
                    display="flex"
                    flexDirection={["column", null, "row"]}
                    variant="unstyledWithHover"
                    size="md"
                    leftIcon={<BundleIcon name="Globe" width="24px" height="24px" />}
                />
            )}
            {linkedin && (
                <SCLinkButton
                    linkField={linkedin}
                    display="flex"
                    flexDirection={["column", null, "row"]}
                    variant="unstyledWithHover"
                    size="md"
                    leftIcon={<BundleIcon name="Linkedin" width="24px" height="24px" />}
                />
            )}
            {downloadLink && (
                <SCLinkButton
                    linkField={downloadLink}
                    display="flex"
                    flexDirection={["column", null, "row"]}
                    variant="unstyledWithHover"
                    size="md"
                    leftIcon={<BundleIcon name="Download" width="24px" height="24px" />}
                />
            )}
        </Flex>
    );
};

const PartnerLayout = (partner: PartnerResult) => {
    const logo = partner.logo;
    const videoUrl = (partner.video?.jsonValue?.value as string) ?? "";

    const {
        sitecoreContext: { pageEditing },
    } = useSitecoreContext();

    const thumbnail = {
        thumbnailAlt: (partner.partnerName?.jsonValue?.value as string) ?? "",
        thumbnailImage: partner.thumbnailImage?.jsonValue ?? undefined,
        thumbnailUrl: (partner.thumbnailUrl?.jsonValue?.value as string) ?? "",
    };

    return (
        <Flex
            gap={["sp32", null, "sp80"]}
            flexDirection={["column-reverse", null, "row"]}
            py={"sp40"}
        >
            <Flex flexDirection="column" width={["100%", null, "50%"]} gap="sp24">
                <Box w="50%" maxH="200px">
                    <SCImage
                        imageField={logo.jsonValue}
                        width={parseInt(logo.jsonValue.value?.width as string) ?? 200}
                        height={parseInt(logo.jsonValue.value?.height as string) ?? 80}
                        display={["none", null, "block"]}
                        mb={["sp16", null, "0"]}
                        w="auto"
                        h="auto"
                        maxH="100%"
                        maxW="100%"
                    />
                </Box>
                {pageEditing && (
                    <Flex flexDirection="column" gap="sp16" mb="sp16">
                        <Flex flexDirection="column">
                            Thumbnail Image URL:
                            <SCText
                                textField={partner.thumbnailUrl.jsonValue}
                                border="1px solid black"
                                mt="sp12"
                                wordBreak="break-word"
                            />
                        </Flex>
                        <Flex flexDirection="column">
                            Video URL:
                            <SCText
                                textField={partner.video.jsonValue}
                                border="1px solid black"
                                mt="sp12"
                                wordBreak="break-word"
                            />
                        </Flex>
                    </Flex>
                )}

                {videoUrl && <VideoPlayer videoUrl={videoUrl} thumbnail={thumbnail} />}

                <Flex flexDirection="column" gap="sp12">
                    <SCHeading as="h3" size="h5" textField={partner?.teaserHeadline.jsonValue} />

                    <SCText size="body" textField={partner?.teaserText.jsonValue} />
                </Flex>

                <Links partner={partner} display={["flex", null, "none"]} />
            </Flex>

            <Flex flexDirection="column" width={["100%", null, "50%"]} gap={["sp16", null, "sp24"]}>
                <SCHeading as="h2" size="h3" mb="0" textField={partner?.headline.jsonValue} />

                <SCText size="h5" textField={partner?.subHeadline.jsonValue} />

                <SCRichText richTextField={partner?.text.jsonValue} />

                <Links partner={partner} display={["none", null, "flex"]} />
            </Flex>
        </Flex>
    );
};

export default PartnerLayout;
